import { useRef } from "react";
import { Description, Dialog, DialogPanel, DialogTitle } from "@headlessui/react";

interface MobileBrowserRecordingWarningProps {
    isOpen: boolean;
    storeUrl: string;
    onConfirm: () => void;
}

const noop = () => {};

// EN-1383: Deliberately cannot be dismissed: onClose is a no-op (Escape and outside
// clicks do nothing) and there is no Cancel or close control. It closes on confirm, or when the
// provider aborts the start (route change, PIN lock, which also hides it). The PIN lock
// screen (z-[100]) stays above this z-50 dialog.
const MobileBrowserRecordingWarning = ({ isOpen, storeUrl, onConfirm }: MobileBrowserRecordingWarningProps) => {
    const confirmRef = useRef<HTMLButtonElement>(null);

    return (
        <Dialog open={isOpen} onClose={noop} role="alertdialog" initialFocus={confirmRef} className="relative z-50">
            <div
                data-testid="mobile-recording-warning-backdrop"
                className="fixed inset-0 bg-shadow-950/70"
                aria-hidden="true"
            />
            <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-start justify-center px-4 py-6 sm:items-center">
                    <DialogPanel className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl">
                        <div className="flex items-center gap-3 bg-blaze-50 px-6 py-5">
                            <svg
                                width="26"
                                height="26"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="text-blaze-900"
                                aria-hidden="true"
                            >
                                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                <path d="M12 9v4" />
                                <path d="M12 17h.01" />
                            </svg>
                            <span className="text-[13px] font-bold uppercase tracking-[0.06em] text-blaze-900">
                                Not supported
                            </span>
                        </div>
                        <div className="flex flex-col gap-3.5 px-6 pb-6 pt-5">
                            <DialogTitle className="fraunces-soft m-0 text-[22px] font-semibold leading-7 text-shadow-950">
                                You may lose parts of this visit
                            </DialogTitle>
                            <Description className="m-0 text-[15px] leading-[22px] text-shadow-700">
                                Phone and tablet browsers stop the microphone when the screen locks or you switch apps.
                                Those minutes are missing from the recording and from your notes. The HappyDoc app keeps
                                recording in the background.
                            </Description>
                            <a
                                href={storeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex h-12 items-center justify-center gap-2 rounded-xl bg-max-900 text-[15px] font-semibold text-white no-underline hover:bg-max-950"
                            >
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                >
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" x2="12" y1="15" y2="3" />
                                </svg>
                                Get the HappyDoc app
                            </a>
                            <div className="flex flex-col gap-2 rounded-xl bg-shadow-25 px-4 py-3.5">
                                <div className="text-sm font-semibold text-shadow-900">If you record here anyway</div>
                                <ol className="m-0 flex list-none flex-col gap-2 p-0 text-sm leading-5 text-shadow-700">
                                    {[
                                        "Keep the screen on and unlocked",
                                        "Keep this tab open in front",
                                        "Check the transcript before generating notes",
                                    ].map((tip, i) => (
                                        <li key={tip} className="flex gap-2">
                                            <span className="flex-none text-shadow-700">{i + 1}.</span>
                                            <span>{tip}</span>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                            <button
                                ref={confirmRef}
                                type="button"
                                onClick={onConfirm}
                                className="h-12 rounded-xl border border-blaze-700 bg-white text-[15px] font-semibold text-blaze-800 hover:bg-blaze-25"
                            >
                                I understand, record in browser
                            </button>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
};

export default MobileBrowserRecordingWarning;
